/**
 * @file DocumentTemplatesPage.tsx
 * @description Шаблоны документов — раздел «Настроек» (phase09; phase19.31,
 * часть 3 — компактный список вместо плиток с кусками HTML-кода).
 *
 * Заголовок — в верхней панели (`CabinetPageHeader`) с пояснением «Шаблоны
 * печатных форм: счёт, УПД, коммерческое предложение», «+ Шаблон» — главное
 * действие (маршрут уже требует право настройки CRM). «Архивные» — пилюля-
 * переключатель вместо нативного флажка. Список — `TemplateList`: строка
 * открывает окно редактирования, «Удалить» — в «⋯», с подтверждением окном
 * кабинета. Список после изменений перечитывается с сервера.
 */

import { FileText, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { TemplateList } from "../../../../components/crm/documents/TemplateList";
import { DocumentTemplateEditDialog } from "../../../../components/crm/DocumentTemplateEditDialog";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { FilterToggleChips } from "../../../../components/ui/FilterChips";
import { ListEmpty, ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import { useConfirm } from "../../../../components/ui/use-confirm";
import { ApiError } from "../../../../lib/api-client";
import { deleteDocumentTemplate, listDocumentTemplates, type DocumentTemplateDto } from "../../../../lib/api-crm";

/**
 * Ошибка удаления человеческим текстом. Сервер на шаблон с документами
 * отвечает `template_in_use` с техническим советом «isActive=false» —
 * переводим в действие кабинета.
 */
function deleteErrorText(err: unknown): string {
  if (err instanceof ApiError && err.code === "template_in_use") {
    return "По этому шаблону уже выпущены документы — удалить его нельзя. Откройте шаблон и снимите флажок «Активен», чтобы убрать его в архив";
  }
  return err instanceof ApiError ? err.message : "Не удалось удалить шаблон";
}

export function DocumentTemplatesPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  const [items, setItems] = useState<DocumentTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<DocumentTemplateDto | "new" | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDocumentTemplates({ includeInactive });
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить шаблоны");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDelete(t: DocumentTemplateDto) {
    const ok = await confirm.confirm({
      title: `Удалить шаблон «${t.name}»?`,
      description: "Шаблон удалится насовсем. Если по нему уже выпускали документы, удалить его нельзя — тогда уберите его в архив.",
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocumentTemplate(t.id);
      notify.success("Шаблон удалён");
      void refresh();
    } catch (err) {
      notify.error(deleteErrorText(err));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Шаблоны документов"
        subtitle="Шаблоны печатных форм: счёт, УПД, коммерческое предложение"
        action={
          <Button variant="brand" onClick={() => setEditing("new")}>
            <Plus className="size-4" />
            Шаблон
          </Button>
        }
      />

      <FilterToggleChips
        aria-label="Архивные шаблоны"
        items={[{ key: "archived", label: "Архивные", pressed: includeInactive, onToggle: () => setIncludeInactive((v) => !v) }]}
      />

      {error && items.length === 0 ? (
        <ListError message={error} onRetry={() => void refresh()} />
      ) : loading && items.length === 0 ? (
        <ListSkeleton rows={3} compact />
      ) : items.length === 0 ? (
        <ListEmpty
          filtered={includeInactive}
          spec={
            includeInactive
              ? { icon: Search, title: "Шаблонов нет и в архиве", onReset: () => setIncludeInactive(false), resetLabel: "Только активные" }
              : { icon: FileText, title: "Шаблонов пока нет", description: "Создайте первый кнопкой «+ Шаблон»: счёт, УПД или коммерческое предложение" }
          }
        />
      ) : (
        <TemplateList templates={items} onEdit={(t) => setEditing(t)} onDelete={(t) => void onDelete(t)} />
      )}

      {editing ? (
        <DocumentTemplateEditDialog
          key={editing === "new" ? "new" : editing.id}
          mode={editing === "new" ? "create" : "edit"}
          template={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
    </div>
  );
}
